/** One subscription per scoped key; late callbacks cannot repopulate a disposed account. */
export interface ResourceState<T> {
  data: T | null;
  confirmed: boolean;
  updatedAt: number | null;
  error: string | null;
}
export type Watch<T> = (next: (data: T, confirmed: boolean) => void, error: (error: unknown) => void) => () => void;
export class LiveResource<T> {
  private state: ResourceState<T> = { data: null, confirmed: false, updatedAt: null, error: null };
  private listeners = new Set<() => void>();
  private stop?: () => void;
  private generation = 0;
  private retry?: ReturnType<typeof setTimeout>;
  private failures = 0;
  private disposed = false;
  constructor(private watch: Watch<T>, private changed: () => void = () => {}, private idle: () => void = () => {}) {}
  isActive = () => this.listeners.size > 0;
  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    if (!this.stop && !this.retry && !this.disposed) this.start();
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) {
        ++this.generation; this.stop?.(); this.stop = undefined;
        if (this.retry) clearTimeout(this.retry);
        this.retry = undefined;
        this.idle();
      }
    };
  };
  private emit() { this.changed(); this.listeners.forEach((listener) => listener()); }
  private start() {
    const generation = ++this.generation;
    // A placeholder also prevents synchronous test/cache callbacks from starting twice.
    this.stop = () => {};
    const next = (data: T, confirmed: boolean) => {
      if (this.disposed || generation !== this.generation) return;
      this.failures = 0;
      // An empty local cache has not established an authoritative zero.
      if (!confirmed && this.state.data === null && Array.isArray(data) && !data.length) return;
      // Do not replace confirmed data with an incomplete offline/cache snapshot.
      if (!confirmed && this.state.confirmed) return;
      this.state = { data, confirmed, updatedAt: confirmed ? Date.now() : this.state.updatedAt, error: null };
      this.emit();
    };
    const failed = (error: any) => {
      if (this.disposed || generation !== this.generation) return;
      this.state = { ...this.state, error: error?.code || error?.message || 'Update failed' };
      this.emit();
      const terminal = /permission-denied|unauthenticated|invalid-argument/.test(this.state.error!);
      if (!terminal) {
        this.retry = setTimeout(() => { this.retry = undefined; this.restart(); }, Math.min(60_000, 1000 * 2 ** this.failures++) + Math.random() * 500);
      }
    };
    let stop = () => {};
    try { stop = this.watch(next, failed); } catch (error) { failed(error); }
    if (this.disposed || generation !== this.generation) stop();
    else this.stop = stop;
  }
  restart = () => {
    if (this.disposed) return;
    if (this.retry) clearTimeout(this.retry);
    this.retry = undefined;
    ++this.generation;
    this.stop?.();
    this.stop = undefined;
    if (this.listeners.size) this.start();
  };
  dispose() {
    this.disposed = true;
    ++this.generation;
    if (this.retry) clearTimeout(this.retry);
    this.stop?.();
    this.stop = undefined;
    this.listeners.clear();
  }
}
